import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('kylo_token');
    if (token) {
      checkAuth(token);
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async (token) => {
    try {
      const response = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      
      // Also fetch subscription status
      const subResponse = await axios.get(`${API}/api/auth/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(subResponse.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('kylo_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password, discountCode = null, region = null) => {
    const payload = { name, email, password };
    if (discountCode && discountCode.trim()) {
      payload.discount_code = discountCode.trim().toUpperCase();
    }
    if (region === 'us' || region === 'uk') {
      payload.region = region;
    }
    // Auto-attribute the signup to an affiliate if a ref code is on file
    const refCode = localStorage.getItem('kylo_ref_code');
    if (refCode && refCode.trim()) {
      payload.affiliate_code = refCode.trim().toUpperCase();
    }
    const response = await axios.post(`${API}/api/auth/register`, payload);
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('kylo_token', access_token);
    setUser(userData);
    
    // Fetch subscription status
    const subResponse = await axios.get(`${API}/api/auth/subscription-status`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setSubscriptionStatus(subResponse.data);
    
    // Return both user data and token for payment redirect flow
    return { user: userData, token: access_token };
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/api/auth/login`, {
      email,
      password
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('kylo_token', access_token);
    setUser(userData);
    
    // Fetch subscription status
    const subResponse = await axios.get(`${API}/api/auth/subscription-status`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setSubscriptionStatus(subResponse.data);
    
    return { user: userData, token: access_token };
  };

  const logout = () => {
    localStorage.removeItem('kylo_token');
    setUser(null);
    setSubscriptionStatus(null);
  };

  // Exchange an Emergent Google OAuth session_id for our app JWT.
  const googleSession = async (sessionId) => {
    const payload = { session_id: sessionId };
    const refCode = localStorage.getItem('kylo_ref_code');
    if (refCode && refCode.trim()) payload.affiliate_code = refCode.trim().toUpperCase();
    const pendingDiscount = localStorage.getItem('poettik_pending_discount');
    if (pendingDiscount && pendingDiscount.trim()) payload.discount_code = pendingDiscount.trim().toUpperCase();

    const response = await axios.post(`${API}/api/auth/google/session`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('kylo_token', access_token);
    setUser(userData);
    localStorage.removeItem('poettik_pending_discount');

    const subResponse = await axios.get(`${API}/api/auth/subscription-status`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    setSubscriptionStatus(subResponse.data);
    return { user: userData, token: access_token };
  };

  const upgradeToPro = async () => {
    const token = localStorage.getItem('kylo_token');
    const response = await axios.post(`${API}/api/auth/upgrade`, 
      { payment_method: 'card' },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    // Refresh user data
    await checkAuth(token);
    return response.data;
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('kylo_token');
    if (token) {
      await checkAuth(token);
    }
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('kylo_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const isSubscriptionActive = () => {
    return subscriptionStatus?.is_active || false;
  };

  const getTrialDaysLeft = () => {
    if (!subscriptionStatus?.expires_at || subscriptionStatus?.subscription_type !== 'trial') {
      return null;
    }
    const expiresAt = new Date(subscriptionStatus.expires_at);
    const now = new Date();
    const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysLeft);
  };

  const value = {
    user,
    loading,
    subscriptionStatus,
    register,
    login,
    googleSession,
    logout,
    upgradeToPro,
    refreshUser,
    getAuthHeader,
    isSubscriptionActive,
    getTrialDaysLeft,
    isAuthenticated: !!user,
    isPro: user?.is_pro || false,
    isBasic: user?.is_basic || false,
    emailVerified: user?.email_verified !== false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
